import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useClickAway } from "react-use";
import { AnimatePresence, motion } from "framer-motion";
import type { LocaleKey } from "../utils/svgPlaceholders";
import type { MenuItem } from "./ColumnRight";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { MenuButton } from "./MenuButton";

interface MobileMenuProps {
  locale: LocaleKey;
  items: MenuItem[];
  onMenuNavigate?: (key: string) => void;
}

export const MobileMenu = ({
  locale,
  items,
  onMenuNavigate,
}: MobileMenuProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const [isOpen, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useClickAway(wrapperRef, () => setOpen(false));

  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const prefix = useMemo(() => (locale === "en" ? "/en" : ""), [locale]);

  const toUrl = (path: string) => {
    const suffix = path ? `/${path}/` : "/";
    return `${prefix}${suffix}`;
  };

  const toggleMenu = () => setOpen((value) => !value);
  const closeMenu = () => setOpen(false);
  const handleItemClick = (key: string) => {
    onMenuNavigate?.(key);
    closeMenu();
  };
  const buttonLabel = isOpen
    ? t("layout.mobileMenu.close")
    : t("layout.mobileMenu.open");
  const buttonCaption = t("layout.mobileMenu.caption");

  return (
    <div ref={wrapperRef} className="app-mobile-menu">
      <motion.div className="app-mobile-menu__trigger">
        <motion.button
          type="button"
          onClick={toggleMenu}
          className={`app-mobile-menu__toggle${
            isOpen ? " app-mobile-menu__toggle--open" : ""
          }`}
          aria-expanded={isOpen}
          aria-controls="mobile-nav-panel"
          aria-label={buttonLabel}
          whileHover={{ scale: [null, 1.2, 1.1] }}
          whileTap={{ scale: 1.05 }}
        >
          <span className="app-mobile-menu__icon" aria-hidden="true">
            <motion.span
              className="app-mobile-menu__bar"
              initial={false}
              animate={
                isOpen
                  ? { rotate: 45, y: 0 }
                  : { rotate: 0, y: -6 }
              }
              transition={{ type: "spring", stiffness: 540, damping: 28 }}
            />
            <motion.span
              className="app-mobile-menu__bar app-mobile-menu__bar--middle"
              initial={false}
              animate={
                isOpen
                  ? { opacity: 0 }
                  : { opacity: 1, y: 0 }
              }
              transition={{ type: "spring", stiffness: 540, damping: 28 }}
            />
            <motion.span
              className="app-mobile-menu__bar"
              initial={false}
              animate={
                isOpen
                  ? { rotate: -45, y: 0 }
                  : { rotate: 0, y: 6 }
              }
              transition={{ type: "spring", stiffness: 540, damping: 28 }}
            />
          </span>
        </motion.button>
        <span className="app-mobile-menu__caption">{buttonCaption}</span>
      </motion.div>

      <AnimatePresence>
        {isOpen ? (
          <>
            <motion.div
              key="mobile-menu-scrim"
              className="app-mobile-menu__scrim"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={closeMenu}
            />
            <motion.nav
              key="mobile-menu-panel"
              id="mobile-nav-panel"
              aria-label={t("layout.mobileMenu.title")}
              className="app-mobile-menu__panel"
              initial={{ opacity: 0, scale: 0.85, y: -12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: -12 }}
              transition={{ type: "spring", stiffness: 540, damping: 28 }}
            >
              <p className="app-mobile-menu__title">
                {t("layout.mobileMenu.title")}
              </p>
              <ul className="app-mobile-menu__list">
                {items.map((item, index) => (
                  <motion.li
                    key={`${item.key}-mobile`}
                    className="app-mobile-menu__list-item"
                    initial={{ opacity: 0, x: 18 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{
                      delay: 0.05 + index * 0.05,
                      duration: 0.18,
                    }}
                  >
                    <MenuButton
                      to={toUrl(item.path)}
                      label={item.label}
                      locale={locale}
                      accent={item.accent}
                      graphic={item.graphic}
                      onClick={() => handleItemClick(item.key)}
                    />
                  </motion.li>
                ))}
                <motion.li
                  key="language-switch"
                  className="app-mobile-menu__list-item app-mobile-menu__list-item--language"
                  initial={{ opacity: 0, x: 18 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{
                    delay: 0.05 + items.length * 0.05,
                    duration: 0.18,
                  }}
                >
                  <div className="app-mobile-menu__language">
                    <LanguageSwitcher locale={locale} />
                  </div>
                </motion.li>
              </ul>
            </motion.nav>
          </>
        ) : null}
      </AnimatePresence>
    </div>
  );
};
